import vueCookie from 'vue-cookie'
import router from '@/router'
import http from '@/http'
import i18n from '@/i18n'
import User from '@/store/models/User'
import Organization from '@/store/models/Organization'
import LicensedPlan from '@/store/models/LicensedPlan'
import { serializeObjectToQueryParameter } from '@/store/helper'

const API_PATH = 'auth/'

const defaultState = () => {
  return {
    initialized: false,
    currentOrganizationId: null,
    signedInUserId: null
  }
}

const state = defaultState()

const getters = {
  initialized: state => state.initialized,
  currentOrganizationId: state => state.currentOrganizationId,
  signedInUser: state => User.find(state.signedInUserId),
  currentOrganization: state => Organization.find(state.currentOrganizationId),
  currentLicensedPlan: () => LicensedPlan.query().first()
}

const mutations = {
  initialized(state) {
    state.initialized = true
  },
  currentOrganizationId(state, payload) {
    state.currentOrganizationId = payload
  },
  signedInUserId(state, payload) {
    state.signedInUserId = payload
  },
  resetModule(state) {
    Object.assign(state, { ...defaultState(), ...{
      initialized: true
    }})
  }
}

const setupDefaultAuthorizedData = (args, callback) => {
  const { data, defaultOrganizationId } = args
  User.insert({
    data: Object.assign(data.data.attributes, {
      favoriteUsers: data.included.filter(resource => resource.type === 'userFavoriteUser').map(user => {
        return {
          id: user.attributes.id,
          userId: user.attributes.userId,
          listSequenceNo: user.attributes.listSequenceNo
        }
      })
    })
  })
  Organization.insert({
    data: data.included.filter(resource => resource.type === 'organization').map(organization => organization.attributes)
  })
  const licensedPlanData = data.included.filter(resource => resource.type === 'licensedPlan' && resource.attributes.organizationId == defaultOrganizationId).map(licensedPlan => licensedPlan.attributes)[0]
  if (licensedPlanData) {
    LicensedPlan.insert({
      data: Object.assign(licensedPlanData, {
        roomPlanMasters: data.included.filter(resource => resource.type === 'licensedRoomPlan' && resource.attributes.organizationId == defaultOrganizationId).map(licensedRoomPlan => licensedRoomPlan.attributes),
        seatPlanMasters: data.included.filter(resource => resource.type === 'licensedAdditionalSeatPlan' && resource.attributes.organizationId == defaultOrganizationId).map(licensedAdditionalSeatPlan => licensedAdditionalSeatPlan.attributes)
      })
    })
  }
  if (Organization.find(defaultOrganizationId) != null) {
    callback()
  }
}

// TODO: defaultOrganizationIdが取れない場合は組織に属していない or 組織ライセンスが凍結されているためstateでUI分岐を
const actions = {
  async validate({ dispatch, commit }) {
    let session
    try {
      session = JSON.parse(vueCookie.get('session')) || []
    } catch (e) {
      session = []
    }
    if ('access-token' in session && 'client' in session && 'uid' in session) {
      await http.get(API_PATH + 'validate_token').then(response => {
        commit('signedInUserId', response.data.data.attributes.id)
        commit('currentOrganizationId', response.data.data.attributes.defaultOrganizationId)
        setupDefaultAuthorizedData({
          data: response.data,
          defaultOrganizationId: response.data.data.attributes.defaultOrganizationId
        }, () => {
          dispatch('setupDefaultDispatch', null, { root: true })
        })
      }).catch(error => {
        if (error?.response?.status == 401) {
          const cookieOptions = {}
          if (process.env.NODE_ENV !== 'development' && process.env.VUE_APP_HOST) {
            cookieOptions.domain = process.env.VUE_APP_HOST
          }
          vueCookie.delete('session', cookieOptions)
        } else {
          // TODO: 401以外のエラー(異常系)
          console.error(error)
        }
      }).then(() => {
        commit('initialized')
      })
    } else {
      commit('initialized')
    }
  },
  async signUp({ commit }, payload) {
    await http.post(API_PATH, payload).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async signIn({ dispatch, commit }, payload) {
    await http.post(API_PATH + 'sign_in', payload).then(response => {
      commit('signedInUserId', response.data.data.attributes.id)
      commit('currentOrganizationId', response.data.data.attributes.defaultOrganizationId)
      setupDefaultAuthorizedData({
        data: response.data,
        defaultOrganizationId: response.data.data.attributes.defaultOrganizationId
      }, () => {
        dispatch('setupDefaultDispatch', null, { root: true })
      })
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async signOut({ dispatch, commit }, noTransition) {
    await http.delete(API_PATH + 'sign_out').then(() => {
      commit('signedInUserId', null)
      if (!noTransition) {
        router.push({ name: 'signIn' })
      }
      dispatch('resetAllModulesAndEntities', null, { root: true })
      const cookieOptions = {}
      if (process.env.NODE_ENV !== 'development' && process.env.VUE_APP_HOST) {
        cookieOptions.domain = process.env.VUE_APP_HOST
      }
      vueCookie.delete('session', cookieOptions)
      commit('setUnusable', false, { root: true })
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async validateConfirmation({ commit }, payload) {
    await http.get(API_PATH + 'confirmation' + serializeObjectToQueryParameter(payload)).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async confirmation({ dispatch, commit }, payload) {
    await http.post(API_PATH + 'confirmation', payload).then(response => {
      commit('signedInUserId', response.data.data.attributes.id)
      commit('currentOrganizationId', response.data.data.attributes.defaultOrganizationId)
      setupDefaultAuthorizedData({
        data: response.data,
        defaultOrganizationId: response.data.data.attributes.defaultOrganizationId
      }, () => {
        dispatch('setupDefaultDispatch', null, { root: true })
      })
      commit('setHttpSuccess', i18n.tc('stores.actions.auth.confirmationSuccess'), { root: true })
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async resendConfirmation ({ commit }, payload) {
    await http.post(API_PATH + 'confirmation/resend', payload).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async update({ state, commit }, payload) {
    const params = JSON.stringify(Object.assign(payload, {
      userId: state.signedInUserId
    }))
    await http.put(API_PATH, params).then(response => {
      User.insert({
        data: Object.assign(response.data.data.attributes, {
          favoriteUsers: response.data.included.filter(resource => resource.type === 'userFavoriteUser').map(user => {
            return {
              id: user.attributes.id,
              userId: user.attributes.userId,
              listSequenceNo: user.attributes.listSequenceNo
            }
          })
        })
      })
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async forgotPassword ({ commit }, payload) {
    await http.post(API_PATH + 'password', payload).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async resetPassword({ dispatch, commit }, payload) {
    await http.put(API_PATH + 'password', payload).then(response => {
      commit('signedInUserId', response.data.data.attributes.id)
      commit('currentOrganizationId', response.data.data.attributes.defaultOrganizationId)
      setupDefaultAuthorizedData({
        data: response.data,
        defaultOrganizationId: response.data.data.attributes.defaultOrganizationId
      }, () => {
        dispatch('setupDefaultDispatch', null, { root: true })
      })
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
